import { Injectable, computed, signal } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { createClient, Provider, SupabaseClient } from "@supabase/supabase-js";
import { firstValueFrom, from, tap } from "rxjs";
import { API_BASE_URL } from "./api-base";
import { AuthUser } from "../models/auth.models";
import { SubmissionResult } from "./submission.service";

interface SignupPayload {
  name: string;
  email: string;
  phone?: string | null;
  password: string;
}

interface PublicAuthConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
}

export interface AuthActionResult {
  requiresEmailVerification?: boolean;
  message?: string;
}

export interface PasswordResetResult {
  message: string;
}

@Injectable({ providedIn: "root" })
export class AuthService {
  private readonly tokenKey = "streamquest_token";
  private readonly refreshTokenKey = "streamquest_refresh_token";
  private readonly tokenExpiryKey = "streamquest_token_expires_at";
  private readonly lastActiveKey = "streamquest_last_active";
  private readonly idleTimeoutMs = 4 * 60 * 60 * 1000;
  private readonly userSignal = signal<AuthUser | null>(null);
  private readonly readySignal = signal(false);
  private readonly startupErrorSignal = signal("");
  private supabase: SupabaseClient | null = null;
  private initializePromise: Promise<void> | null = null;
  private idleTimer: ReturnType<typeof setInterval> | null = null;
  private activityListenersRegistered = false;

  readonly user = computed(() => this.userSignal());
  readonly isLoggedIn = computed(() => Boolean(this.userSignal()));
  readonly isReady = computed(() => this.readySignal());
  readonly startupError = computed(() => this.startupErrorSignal());

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router
  ) {}

  initialize(): Promise<void> {
    if (!this.initializePromise) {
      this.initializePromise = this.initializeInternal();
    }
    return this.initializePromise;
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  touchActivity() {
    if (!this.getToken()) {
      return;
    }
    localStorage.setItem(this.lastActiveKey, String(Date.now()));
  }

  signup(payload: SignupPayload) {
    return from(this.signupInternal(payload));
  }

  login(identifier: string, password: string) {
    return from(this.loginInternal(identifier, password));
  }

  loginWithProvider(provider: Provider) {
    return from(this.loginWithProviderInternal(provider));
  }

  changePassword(email: string, currentPassword: string, newPassword: string) {
    return from(this.changePasswordInternal(email, currentPassword, newPassword));
  }

  requestPasswordReset(email: string) {
    return from(this.requestPasswordResetInternal(email));
  }

  confirmPasswordReset(accessToken: string, refreshToken: string, newPassword: string) {
    return from(this.confirmPasswordResetInternal(accessToken, refreshToken, newPassword));
  }

  fetchMe() {
    return this.http.get<AuthUser>(`${API_BASE_URL}/auth/me`).pipe(
      tap((user) => this.userSignal.set(user))
    );
  }

  syncStats(data: { xp: number; level: number; streak: number }) {
    const user = this.userSignal();
    if (!user) {
      return;
    }
    this.userSignal.set({
      ...user,
      xp: data.xp,
      level: data.level,
      streak: data.streak
    });
  }

  applySubmissionRewards(result: SubmissionResult) {
    if (!result.rewards) {
      return;
    }
    const user = this.userSignal();
    if (!user) {
      return;
    }
    this.userSignal.set({
      ...user,
      xp: user.xp + result.rewards.gainedXp,
      level: result.rewards.level,
      streak: result.rewards.streak
    });
  }

  logout() {
    void this.logoutInternal(true);
  }

  private async initializeInternal() {
    try {
      const config = await firstValueFrom(this.http.get<PublicAuthConfig>(`${API_BASE_URL}/auth/config`));
      const storage = {
        getItem: (key: string) => localStorage.getItem(key),
        setItem: (key: string, value: string) => localStorage.setItem(key, value),
        removeItem: (key: string) => localStorage.removeItem(key)
      };
      this.supabase = createClient(config.supabaseUrl, config.supabaseAnonKey, {
        auth: {
          storage,
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false
        }
      });

      this.registerActivityListeners();
      this.startIdleMonitor();

      const existingToken = this.getToken();
      const refreshToken = this.getRefreshToken();
      if (existingToken && !this.isIdleExpired()) {
        this.ensureActivityTimestamp();
        if (refreshToken) {
          void this.refreshSession().catch(() => {
            // Keep the local session and retry later if refresh fails due to a transient error.
          });
        } else {
          void this.applySession(existingToken).catch(() => {
            // applySession handles auth failures; avoid clearing tokens on transient errors.
          });
        }
      } else {
        this.clearLocalAuth(false);
      }
    } catch (error) {
      this.startupErrorSignal.set(this.readError(error, "Supabase auth is not configured"));
      this.initializePromise = null;
    } finally {
      this.readySignal.set(true);
    }
  }

  private async ensureInitialized() {
    await this.initialize();
    if (!this.supabase) {
      throw new Error(this.startupErrorSignal() || "Supabase auth is not configured");
    }
  }

  private async loginInternal(identifier: string, password: string): Promise<AuthActionResult> {
    await this.ensureInitialized();
    const email = identifier.trim().toLowerCase();
    const { data, error } = await this.supabase!.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      throw error;
    }
    if (!data.session?.access_token) {
      throw new Error("Unable to start a session");
    }

    await this.applySession(
      data.session.access_token,
      data.session.refresh_token,
      data.session.expires_at
    );
    return {};
  }

  private async signupInternal(payload: SignupPayload): Promise<AuthActionResult> {
    await this.ensureInitialized();
    const redirectTo = `${window.location.origin}/auth`;
    const { data, error } = await this.supabase!.auth.signUp({
      email: payload.email.trim().toLowerCase(),
      password: payload.password,
      options: {
        emailRedirectTo: redirectTo,
        data: {
          name: payload.name,
          phone: payload.phone || undefined
        }
      }
    });

    if (error) {
      throw error;
    }

    if (data.session?.access_token) {
      await this.applySession(
        data.session.access_token,
        data.session.refresh_token,
        data.session.expires_at
      );
      return {
        message: "Account created successfully."
      };
    }

    this.clearLocalAuth(false);
    return {
      requiresEmailVerification: true,
      message: "Verification email sent. Open the link in your inbox, then sign in."
    };
  }

  private async loginWithProviderInternal(provider: Provider) {
    await this.ensureInitialized();
    const { error } = await this.supabase!.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth`
      }
    });

    if (error) {
      throw error;
    }
  }

  private async requestPasswordResetInternal(email: string): Promise<PasswordResetResult> {
    await this.ensureInitialized();
    const { error } = await this.supabase!.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/reset-password`
    });
    if (error) {
      throw error;
    }
    return {
      message: "Password reset email sent. Check your inbox."
    };
  }

  private async confirmPasswordResetInternal(accessToken: string, refreshToken: string, newPassword: string) {
    await this.ensureInitialized();
    const { error: sessionError } = await this.supabase!.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken
    });
    if (sessionError) {
      throw sessionError;
    }
    const { error } = await this.supabase!.auth.updateUser({
      password: newPassword
    });
    if (error) {
      throw error;
    }

    await this.applySession(accessToken, refreshToken);
    return {
      message: "Password updated. You are now signed in."
    };
  }

  private async changePasswordInternal(email: string, currentPassword: string, newPassword: string) {
    await this.ensureInitialized();
    const { data, error } = await this.supabase!.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password: currentPassword
    });
    if (error) {
      throw error;
    }
    if (!data.session?.access_token) {
      throw new Error("Unable to re-authenticate.");
    }
    const { error: updateError } = await this.supabase!.auth.updateUser({
      password: newPassword
    });
    if (updateError) {
      throw updateError;
    }
    await this.applySession(
      data.session.access_token,
      data.session.refresh_token,
      data.session.expires_at
    );
    return {
      message: "Password updated successfully."
    };
  }

  private async applySession(accessToken: string, refreshToken?: string | null, expiresAt?: number | null) {
    this.persistSession(accessToken, refreshToken, expiresAt);
    try {
      await firstValueFrom(this.fetchMe());
      this.startupErrorSignal.set("");
      this.touchActivity();
    } catch (error) {
      if (this.isAuthFailure(error)) {
        this.clearLocalAuth(false);
      } else {
        this.startupErrorSignal.set(this.readError(error, "Unable to load your profile"));
      }
      throw error;
    }
  }

  async refreshSession(): Promise<boolean> {
    await this.ensureInitialized();
    if (!this.supabase) {
      return false;
    }
    const accessToken = this.getToken();
    const refreshToken = this.getRefreshToken();
    if (!accessToken || !refreshToken) {
      return false;
    }

    const { data, error } = await this.supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken
    });

    if (error || !data.session?.access_token) {
      return false;
    }

    await this.applySession(
      data.session.access_token,
      data.session.refresh_token,
      data.session.expires_at
    );
    return true;
  }

  private async logoutInternal(navigate: boolean) {
    try {
      await this.supabase?.auth.signOut();
    } catch (error) {
      // Ignore Supabase sign-out failures and clear the local session regardless.
    }
    this.clearLocalAuth(navigate);
  }

  private clearLocalAuth(navigate: boolean) {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.tokenExpiryKey);
    localStorage.removeItem(this.lastActiveKey);
    this.userSignal.set(null);
    if (navigate) {
      void this.router.navigateByUrl("/auth");
    }
  }

  private isIdleExpired() {
    const lastActive = Number(localStorage.getItem(this.lastActiveKey) || 0);
    if (!lastActive) {
      return false;
    }
    return Date.now() - lastActive > this.idleTimeoutMs;
  }

  private ensureActivityTimestamp() {
    const lastActive = Number(localStorage.getItem(this.lastActiveKey) || 0);
    if (!lastActive) {
      this.touchActivity();
    }
  }

  private persistSession(accessToken: string, refreshToken?: string | null, expiresAt?: number | null) {
    localStorage.setItem(this.tokenKey, accessToken);
    if (refreshToken) {
      localStorage.setItem(this.refreshTokenKey, refreshToken);
    }
    if (expiresAt) {
      localStorage.setItem(this.tokenExpiryKey, String(expiresAt));
    }
  }

  private isAuthFailure(error: unknown) {
    return error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403);
  }

  private startIdleMonitor() {
    if (this.idleTimer) {
      return;
    }
    this.idleTimer = setInterval(() => {
      if (!this.getToken()) {
        return;
      }
      if (this.isIdleExpired()) {
        void this.logoutInternal(true);
      }
    }, 60_000);
  }

  private registerActivityListeners() {
    if (this.activityListenersRegistered || typeof window === "undefined") {
      return;
    }
    const handler = () => this.touchActivity();
    window.addEventListener("pointerdown", handler, { passive: true });
    window.addEventListener("keydown", handler, { passive: true });
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        this.touchActivity();
      }
    });
    this.activityListenersRegistered = true;
  }

  private readError(error: unknown, fallback: string) {
    if (error instanceof Error && error.message) {
      return error.message;
    }
    const candidate = error as { error?: { message?: string }; message?: string } | null;
    return candidate?.error?.message || candidate?.message || fallback;
  }
}
